import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { FaArrowLeft, FaFileUpload, FaDownload, FaSave } from "react-icons/fa";
import api from "../../services/api";
import { ehPlanoGratuito } from "../../services/seguranca";
import { useAppSelector } from "../../store/hooks";
import styles from "./DocxEditorV2.module.css";

declare global {
  interface Window {
    DocsAPI?: any;
  }
}

const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

function carregarScriptDocsApi(documentServerUrl: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.DocsAPI) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = `${documentServerUrl}/web-apps/apps/api/documents/api.js`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Falha ao carregar o editor de documentos"));
    document.body.appendChild(script);
  });
}

export default function DocxEditorV2() {
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [nomeDocumento, setNomeDocumento] = useState("");
  const editorRef = useRef<any>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const token = useAppSelector(state => state.token);
  const logado = useAppSelector(state => state.logado);
  const expirado = useAppSelector(state => state.expirado);
  const bloqueadoPorInadimplencia = useAppSelector(state => state.bloqueadoPorInadimplencia);

  const history = useHistory();
  const location = useLocation();
  const idModeloContrato = (location.state as any)?.idModeloContrato;

  useEffect(() => {
    if (!logado && !token) {
      history.replace('/login/acessobloqueado');
      return;
    }
    if (expirado) {
      history.replace('/login');
      return;
    }
    if (bloqueadoPorInadimplencia) {
      history.replace('/renovar');
      return;
    }
    if (ehPlanoGratuito(token)) {
      history.push('/modelo-contrato');
      setTimeout(() => {
        toast.info('Apenas contas no plano Premium podem gerar contratos automáticos.');
      }, 300);
    }
  }, [logado, token, expirado, bloqueadoPorInadimplencia, history]);

  const destruirEditor = useCallback(() => {
    if (editorRef.current) {
      try {
        editorRef.current.destroyEditor();
      } catch {
        /* editor pode já ter sido destruído */
      }
      editorRef.current = null;
    }
  }, []);

  const montarEditor = useCallback(async (): Promise<boolean> => {
    setErro(null);
    setCarregando(true);
    try {
      const [configResponse, modeloResponse] = await Promise.all([
        api.get(`/ModeloContrato/ConfigEditor/${idModeloContrato}`),
        api.get(`/ModeloContrato/${idModeloContrato}`),
      ]);

      setNomeDocumento(modeloResponse.data.titulo || "Modelo de Contrato");

      await carregarScriptDocsApi(configResponse.data.documentServerUrl);

      destruirEditor();
      editorRef.current = new window.DocsAPI.DocEditor("editor-onlyoffice", {
        ...configResponse.data.config,
        type: "desktop",
        width: "100%",
        height: "100%",
        events: {
          onError: (event: any) => {
            console.error("Erro no editor:", event);
          },
        },
      });

      setCarregando(false);
      return true;
    } catch (error: any) {
      console.error("Erro ao abrir o editor v2:", error);
      if (error?.response?.status === 403) {
        setErro("Sua conta não tem acesso ao novo editor. Fale com o suporte.");
      } else {
        setErro("Não foi possível abrir o editor. Tente novamente em instantes.");
      }
      setCarregando(false);
      return false;
    }
  }, [idModeloContrato, destruirEditor]);

  useEffect(() => {
    if (!idModeloContrato) {
      history.replace('/modelo-contrato');
      return;
    }

    let cancelado = false;
    (async () => {
      const ok = await montarEditor();
      if (cancelado && ok) destruirEditor();
    })();

    return () => {
      cancelado = true;
      destruirEditor();
    };
  }, [idModeloContrato, history, montarEditor, destruirEditor]);

  const handleArquivoSelecionado = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== DOCX_MIME && !file.name.toLowerCase().endsWith(".docx")) {
      toast.error("Selecione um arquivo .docx");
      return;
    }

    setEnviando(true);
    try {
      const formData = new FormData();
      formData.append("documento", file, file.name);
      await api.put(`/ModeloContrato/SalvarDocumento/${idModeloContrato}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Documento carregado. Abrindo no editor...");
      await montarEditor();
    } catch (error) {
      console.error("Erro ao enviar documento:", error);
      toast.error("Não foi possível carregar o documento");
    } finally {
      setEnviando(false);
    }
  };

  const handleSalvar = async () => {
    setSalvando(true);
    let sucesso = false;
    try {
      const resposta = await api.post(`/ModeloContrato/ForcarSalvamentoEditor/${idModeloContrato}`);
      sucesso = resposta.data?.salvou === true;
      if (!sucesso) {
        toast.error("Não foi possível salvar agora. Tente novamente em instantes.");
      }
    } catch {
      toast.error("Não foi possível salvar agora.");
    } finally {
      setSalvando(false);
    }

    if (sucesso && window.confirm("Documento salvo! Deseja voltar para o menu principal?")) {
      history.push('/modelo-contrato');
    }
  };

  const baixarDocx = async () => {
    try {
      const response = await api.get(`/ModeloContrato/${idModeloContrato}`);
      if (response.data?.documento) {
        window.open(response.data.documento, "_blank");
      } else {
        toast.info("Este modelo ainda não possui documento salvo.");
      }
    } catch {
      toast.error("Erro ao baixar o documento");
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <button className={styles.voltar} onClick={() => history.push('/modelo-contrato')}>
          <FaArrowLeft size={13} />
          Voltar
        </button>

        <h2 className={styles.titulo}>{nomeDocumento}</h2>

        <div className={styles.acoes}>
          <button
            className={`${styles.btn} ${styles.btnSecundario}`}
            onClick={() => fileInputRef.current?.click()}
            disabled={enviando}
            title="Enviar um arquivo .docx para este modelo"
          >
            <FaFileUpload size={14} />
            {enviando ? "Enviando..." : "Trocar documento"}
          </button>

          <button className={`${styles.btn} ${styles.btnSecundario}`} onClick={baixarDocx}>
            <FaDownload size={14} />
            Baixar
          </button>

          <button
            className={`${styles.btn} ${styles.btnPrimario}`}
            onClick={handleSalvar}
            disabled={salvando || carregando}
          >
            <FaSave size={14} />
            {salvando ? "Salvando..." : "Salvar"}
          </button>
        </div>

        <input
          type="file"
          ref={fileInputRef}
          accept=".docx"
          style={{ display: "none" }}
          onChange={handleArquivoSelecionado}
        />
      </div>

      <div className={styles.editorHost}>
        <div id="editor-onlyoffice"></div>

        {!erro && (carregando || enviando) && (
          <div className={styles.overlay}>
            <div className={styles.spinner}></div>
            <p>{enviando ? "Enviando documento..." : "Carregando editor..."}</p>
          </div>
        )}

        {erro && (
          <div className={styles.overlay}>
            <p>{erro}</p>
            <button className={`${styles.btn} ${styles.btnSecundario}`} onClick={() => montarEditor()}>
              Tentar novamente
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
